import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import EditSettingsPageView from 'components/settings/EditSettingsPageView';
import { UserContext } from 'components/UserGuard';
import Spinner from 'components/shared/Spinner';
import SiteModule from 'store/enums/SiteModule';
import { getPageTitle } from 'util/Layout';
import { ConfigContext } from 'components/ConfigGuard';
import TabPanel from '@mui/lab/TabPanel';
import { TabContext } from '@mui/lab';
import { Tab, Tabs } from '@mui/material';
import { getTabKeyByRoute, getTabRouteByKey } from 'util/Route';
import { useHistory, useLocation } from 'react-router';
import routes from 'store/configs/Routes';
import ManagePaymentMethodsPageView from 'components/settings/ManagePaymentMethodsPageView';
import EmailNotificationsSettingsPageView from 'components/settings/EmailNotificationsSettingsPageView';
import PortalManagementPageView from '../PortalManagementPageView';
import { getLocalStorageValue, SUPER_ADMIN_FLAG_LS_LABEL } from 'util/LocalStorage';
import { TabItem } from 'store/types/Tabs';
import { getDefaultTabRoute, getVisibleTabs } from 'util/TabHelper';

import commonStyles from 'styles/common.module.scss';

const tabConfig = {
  SettingsAccount: { key: 'SettingsAccount', label: 'Account Settings', route: routes.settingsAccount } as TabItem,
  PaymentMethodsSettings: {
    key: 'PaymentMethodsSettings',
    label: 'Manage Saved Payment',
    route: routes.paymentMethodsSettings,
  } as TabItem,
  EmailNotificationsSettings: {
    key: 'EmailNotificationsSettings',
    label: 'Manage Email Notifications',
    route: routes.emailNotificationsSettings,
  } as TabItem,
  PortalManagementSettings: {
    key: 'PortalManagementSettings',
    label: 'Portal Management',
    route: routes.portalManagementSettings,
  } as TabItem,
};

const SettingsPage: React.FunctionComponent = () => {
  const {
    sidebar,
    enabledModules,
    modulesSettings: {
      settings: { enableAccountSettings, enableEmailNotifications, enablePortalManagement, hiddenTabs },
    },
  } = useContext(ConfigContext);
  const { userLoading = false } = useContext(UserContext);
  const history = useHistory();
  const { pathname } = useLocation();
  const paymentsEnabled: boolean = useMemo(
    () => !!enabledModules.find((item) => item === SiteModule.Payments),
    [enabledModules]
  );

  const activeTab: string = useMemo(() => getTabKeyByRoute(pathname, tabConfig), [pathname]);
  const isUserSuperAdmin = !!getLocalStorageValue(SUPER_ADMIN_FLAG_LS_LABEL);

  const visibleTabs = useMemo(() => {
    return getVisibleTabs(tabConfig, hiddenTabs);
  }, [hiddenTabs]);

  const handleTabChange = useCallback(
    (_: any, value = tabConfig.SettingsAccount) => {
      history.replace(getTabRouteByKey(value, tabConfig));
    },
    [history]
  );

  useEffect(() => {
    history.replace(getDefaultTabRoute(location.pathname, visibleTabs));
  }, [history, visibleTabs]);

  useEffect(() => {
    if (pathname === routes.settings) {
      if (enableAccountSettings) {
        history.replace(tabConfig.SettingsAccount.route);
      } else if (paymentsEnabled) {
        history.replace(tabConfig.PaymentMethodsSettings.route);
      } else if (enablePortalManagement && isUserSuperAdmin) {
        history.replace(tabConfig.PortalManagementSettings.route);
      } else {
        history.replace(tabConfig.EmailNotificationsSettings.route);
      }
    }
  }, [history, pathname, enableAccountSettings, paymentsEnabled, enablePortalManagement, isUserSuperAdmin]);

  return (
    <>
      <h1 className={commonStyles.pageTitle}>{getPageTitle(sidebar, SiteModule.Settings) || 'Account Settings'}</h1>
      <Spinner loading={userLoading} transparent={false}>
        <TabContext value={activeTab}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor={'primary'}
            textColor={'primary'}
            variant={'scrollable'}
          >
            {visibleTabs.map((tab) => {
              if (tab.key === tabConfig.SettingsAccount.key && !enableAccountSettings) {
                return;
              }

              if (tab.key === tabConfig.PaymentMethodsSettings.key && !paymentsEnabled) {
                return;
              }

              if (tab.key === tabConfig.EmailNotificationsSettings.key && !enableEmailNotifications) {
                return;
              }

              if (
                tab.key === tabConfig.PortalManagementSettings.key &&
                (!enablePortalManagement || !isUserSuperAdmin)
              ) {
                return;
              }

              return (
                <Tab
                  key={tab.key}
                  value={tab.key}
                  label={tab.label}
                  className={commonStyles.tab}
                  classes={{ selected: commonStyles.activeTab }}
                />
              );
            })}
          </Tabs>
          {enableAccountSettings && (
            <TabPanel className={commonStyles.tabPanel} value={tabConfig.SettingsAccount.key}>
              <Spinner loading={userLoading} transparent={false}>
                <EditSettingsPageView />
              </Spinner>
            </TabPanel>
          )}
          {paymentsEnabled && (
            <TabPanel className={commonStyles.tabPanel} value={tabConfig.PaymentMethodsSettings.key}>
              <Spinner loading={userLoading} transparent={false}>
                <ManagePaymentMethodsPageView />
              </Spinner>
            </TabPanel>
          )}
          {enableEmailNotifications && (
            <TabPanel className={commonStyles.tabPanel} value={tabConfig.EmailNotificationsSettings.key}>
              <Spinner loading={userLoading} transparent={false}>
                <EmailNotificationsSettingsPageView />
              </Spinner>
            </TabPanel>
          )}
          {enablePortalManagement && isUserSuperAdmin && (
            <TabPanel className={commonStyles.tabPanel} value={tabConfig.PortalManagementSettings.key}>
              <Spinner loading={userLoading} transparent={false}>
                <PortalManagementPageView />
              </Spinner>
            </TabPanel>
          )}
        </TabContext>
      </Spinner>
    </>
  );
};

export default SettingsPage;
